import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injectable, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { EmployeeService, IMesUser } from '@shared/services/mesuser.service';
import { IProductionNote, ProductionNoteService } from '@shared/services/production-note.service';
import { take } from 'rxjs';
import { IBatch, MesBatchService } from '../mes-batch/mes-batch-service';

interface ITableViewModelItem {
	batch: string;
	order: string;
	position: string;
	notes: MatTableDataSource<IProductionNoteViewModel>;
}

interface IProductionNoteViewModel extends IProductionNote {
	createdByName: string;
}

@Component({
	selector: 'app-production-note',
	templateUrl: './production-note.component.html',
	styleUrl: './production-note.component.scss',
	animations: [trigger('detailExpand', [state('collapsed, void', style({ height: '0px' })), state('expanded', style({ height: '*' })), transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')), transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))])],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
@Injectable({ providedIn: 'root' })
export class ProductionNoteComponent implements OnInit {
	protected batches: IBatch[] = [];
	protected employees: IMesUser[] = [];
	public expandedElements: ITableViewModelItem[] = [];
	public showSearch = false;
	public dataSource = new MatTableDataSource<ITableViewModelItem>();

	currentBatch: IBatch;
	order: string;
	position: number;

	@ViewChild(MatSort) sort: MatSort;

	displayedColumns = ['batch', 'order', 'position'];
	noteDisplayedColumns = ['sequenceNumber', 'createdAt', 'createdBy', 'note'];

	constructor(
		public cd: ChangeDetectorRef,
		private batchService: MesBatchService,
		private employeeService: EmployeeService,
		private noteService: ProductionNoteService,
	) {}

	ngOnInit(): void {
		this.dataSource.sort = this.sort;
		this.batchService.getAll().subscribe((batches) => {
			this.batches = batches.result;
		});
		this.employeeService.getEmployees().subscribe((employees) => {
			this.employees = employees.result;
		});
	}

	protected searchByBatch(): void {
		this.order = '';
		this.position = null;
		this.noteService
			.getProductionNotesByBatchId(this.currentBatch.id)
			.pipe(take(1))
			.subscribe((notes) => {
				this.dataSource.data = this.buildTableData(notes, this.batches);
				this.dataSource.sort = this.sort;
			});
	}

	protected searchByOrderPosition(): void {
		this.currentBatch = null;
		const alias = this.position ? `${this.order}:${this.position}` : `${this.order}`;
		this.noteService
			.getProductionNotesByAlias(alias)
			.pipe(take(1))
			.subscribe((notes) => {
				this.dataSource.data = this.buildTableData(notes, this.batches);
				this.dataSource.sort = this.sort;
			});
	}

	private buildTableData(notes: IProductionNote[], batches: IBatch[]): ITableViewModelItem[] {
		const notesWithParsed: (IProductionNote & {
			order: string;
			position: string;
		})[] = notes.map((note) => {
			const parts = note.itemAlias.split(':');
			return {
				...note,
				order: parts[0] ?? '',
				position: parts[1] ?? '',
			};
		});

		const grouped: { [key: string]: typeof notesWithParsed } = notesWithParsed.reduce(
			(acc, note) => {
				const key = `${note.order}:${note.position}`;
				if (!acc[key]) acc[key] = [];
				acc[key].push(note);
				return acc;
			},
			{} as { [key: string]: typeof notesWithParsed },
		);

		const items = Object.entries(grouped).map(([key, notes]) => {
			const [order, position] = key.split(':');

			const batch = batches.find((b) => b.id === notes[0].batchId);
			const batchDisplay = batch ? `(${batch.alias}) (${batch.name})` : notes[0].batchId;
			const notesViewModel = this.sortBy(
				notes.map((n) => this.mapToNoteViewModel(n)),
				(a, b) => a.sequenceNumber - b.sequenceNumber,
				(a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
			);

			return {
				batch: batchDisplay,
				order,
				position,
				notes: new MatTableDataSource(notesViewModel),
			};
		});

		return this.sortBy(
			items,
			(a, b) => Number(a.order) - Number(b.order),
			(a, b) => Number(a.position) - Number(b.position),
		);
	}

	private mapToNoteViewModel(note: IProductionNote): IProductionNoteViewModel {
		const employee = note.createdBy.includes('@') ? null : this.employees.find((e) => e.alias === note.createdBy);

		return {
			...note,
			createdByName: employee ? `${employee.firstName} ${employee.lastName}` : '',
		};
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	public batchChange(event: MatSelectChange): void {
		this.currentBatch = event.value;
	}

	onSortNotes(event: Sort, orderPosition: ITableViewModelItem) {
		const data = orderPosition.notes.data.slice();
		const direction = this.getSortDirection(event);

		if (!event.active || event.direction === '') {
			orderPosition.notes.data = this.sortBy(
				data,
				(a, b) => a.sequenceNumber - b.sequenceNumber,
				(a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
			);
			return;
		}

		orderPosition.notes.data = data.sort((a, b) => {
			switch (event.active) {
				case 'sequenceNumber': {
					const seqCompare = this.compare(a.sequenceNumber, b.sequenceNumber, direction);
					if (seqCompare !== 0) return seqCompare;
					return this.compareDates(a.createdAt, b.createdAt, direction);
				}
				case 'note':
					return this.compare(a.note.toUpperCase(), b.note.toUpperCase(), direction);
				case 'createdBy':
					return this.compare(a.createdByName?.toUpperCase(), b.createdByName?.toUpperCase(), direction);
				case 'createdAt': {
					const dateCompare = this.compareDates(a.createdAt, b.createdAt, direction);
					if (dateCompare !== 0) return dateCompare;
					return this.compare(a.sequenceNumber, b.sequenceNumber, direction);
				}
				default:
					return 0;
			}
		});
	}

	private compareDates(a: string, b: string, direction: 'asc' | 'desc' = 'asc'): -1 | 0 | 1 {
		const dA = new Date(a).getTime();
		const dB = new Date(b).getTime();

		if (dA < dB) return direction === 'asc' ? -1 : 1;
		if (dA > dB) return direction === 'asc' ? 1 : -1;
		return 0;
	}

	private compare(a: string | boolean | number, b: string | boolean | number, direction: 'asc' | 'desc' = 'asc'): -1 | 0 | 1 {
		if (a < b) {
			return direction === 'asc' ? -1 : 1;
		} else if (a > b) {
			return direction === 'asc' ? 1 : -1;
		}
		return 0;
	}

	private sortBy<T>(data: T[], ...comparators: ((a: T, b: T) => number)[]): T[] {
		return data.slice().sort((a, b) => {
			for (const comparator of comparators) {
				const result = comparator(a, b);
				if (result !== 0) return result;
			}
			return 0;
		});
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'order', start: 'asc', disableClear: true });
		}
	}

	private getSortDirection(sort: Sort): 'asc' | 'desc' {
		return sort.direction === 'desc' ? 'desc' : 'asc';
	}

	toggleRow(element: ITableViewModelItem) {
		const index = this.expandedElements.indexOf(element);
		if (index >= 0) {
			this.expandedElements.splice(index, 1);
		} else {
			this.expandedElements.push(element);
		}
		this.cd.detectChanges();
	}
}
